import { useMemo } from "react";
import {
  createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel
} from "@tanstack/react-table";
import { type BusinessUser, type BusinessSubscription } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import { dateToString } from "src/utils/date";
import { getPlanName } from "src/services/plans";
import { type ActiveSeat, isActiveSeat } from "src/services/getSubscriptionSeatState";
import { trimSubscriptionId } from "src/services/trimSubscriptionId";

import { UsernameTableCell } from "src/components/Business/UsernameTableCell";
import Table from "src/components/Table/Table";

import styles from "src/components/Business/ActiveSeatTable.module.css";

import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { useTrackedTableSort } from "./useTrackedTableSort";
import { ContextMenuIcon } from "../Icon/Navigation";
import { useLensPlatformClient } from "../../hooks/useLensPlatformClient";

const { DeleteIcon } = action;

const activeSeatTableColumnHelper = createColumnHelper<ActiveSeat>();

interface Props {
  businessSubscriptions: BusinessSubscription[];
  businessUsers: BusinessUser[];
  onUnassignSeat?: (id: string) => void;
}

const caption = "Active seats";

/**
 * The table to display active (used and not deactivated) seats
 */
const ActiveSeatTable = ({ businessSubscriptions, businessUsers, onUnassignSeat }: Props) => {
  const platformClient = useLensPlatformClient();
  const activeSeatTableColumns = useMemo(() => ([
    activeSeatTableColumnHelper.accessor("user", {
      header: () => <span>Lens ID</span>,
      cell: ({ getValue }) => {
        const user = getValue();

        if (user) {
          const { username, email } = user;
          const fullname = platformClient.user.getUserFullName(user);

          return (
            <UsernameTableCell username={username} fullname={fullname} email={email} />
          );
        }

        return (
          <div className={styles.noUser}>
            <div>N/A</div>
          </div>
        );
      },
      meta: { primary: true }
    }),
    activeSeatTableColumnHelper.accessor("subscription", {
      header: () => <span>Subscription</span>,
      cell: ({ getValue }) => {
        const subscription = getValue();

        if (!subscription) {
          return null;
        }
        const { planCode, id } = subscription;

        return (
          <div className={styles.subscription}>
            <div className={styles.subscriptionPlanName}>{getPlanName(planCode)}</div>
            <div className={styles.subscriptionId} title={id}>{trimSubscriptionId(id)}</div>
          </div>
        );
      }
    }),
    activeSeatTableColumnHelper.accessor("activatedAt", {
      header: () => <span>Activation Date</span>,
      cell: ({ getValue }) => {
        const activatedAt = dateToString(new Date(getValue()));

        return (
          <div className={styles.activatedAt}>
            <div>{activatedAt}</div>
          </div>
        );
      }
    }),
    activeSeatTableColumnHelper.accessor("user", {
      header: () => <span>Last Seen</span>,
      cell: ({ getValue }) => {
        const userId = getValue()?.id;
        const lastAccess = businessUsers.find(({ id }) => id === userId)?.lastAccess;
        const lastAccessInDate = lastAccess ? dateToString(new Date(lastAccess)) : null;

        return (
          <div className={styles.lastSeenAt}>
            <div>{lastAccessInDate ?? "N/A"}</div>
          </div>
        );
      }
    }),
    activeSeatTableColumnHelper.accessor("offline", {
      header: () => <span>Offline</span>,
      cell: ({ getValue }) => (
        <div className={styles.offlineContainer}>
          {getValue() && <span title="Seat used offline" className={styles.offline} />}
        </div>
      )
    }),
    activeSeatTableColumnHelper.accessor("id", {
      header: () => <span />,
      enableSorting: false,
      cell: ({ getValue }) => (
        <div className={styles.menuContainer}>
          <ContextMenu toggleIcon={ContextMenuIcon} toggleButtonProps={{ title: "Toggle context menu" }}>
            <ContextMenuItem label="Unassign" icon={DeleteIcon} onClick={() => onUnassignSeat && onUnassignSeat(getValue())} />
          </ContextMenu>
        </div>
      )
    })
  ]), [onUnassignSeat, platformClient.user, businessUsers]);

  const activeSeats = useMemo<ActiveSeat[]>(() => {
    const useSeats = businessSubscriptions
      .map(({ usedSeats }) => usedSeats)
      .flat()
      .filter(isActiveSeat);

    return useSeats.map((usedSeat) => ({
      ...usedSeat,
      subscription: businessSubscriptions.find(({ id }) => id === usedSeat.subscriptionId)
    }));
  }, [businessSubscriptions]);

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const activeTable = useReactTable({
    data: activeSeats,
    columns: activeSeatTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const activeTableRowModel = activeTable.getRowModel();
  const activeTableRows = useMemo(() => activeTableRowModel.rows, [activeTableRowModel]);

  return (
    <Table<ActiveSeat>
      caption={caption}
      className={styles.table}
      rows={activeTableRows}
      columns={activeTable.getAllColumns()}
      headerGroups={activeTable.getHeaderGroups()}
      headerGroupProps={{
        cellClassName: styles.tableHeader
      }}
      rowProps={{
        className: styles.tableRow,
        cellClassName: styles.tableCell
      }}
      noDataText="No active seats on your Lens Business ID!"
    />
  );
};

export default ActiveSeatTable;
